import { useEffect, useMemo, useRef, useState } from 'react';
import { allLeaves } from './layout';
import type { Layout, NavItem, OpenTabFn } from './types';

type Kind = 'Action' | 'Navigation';

interface PaletteItem {
  id: string;
  kind: Kind;
  title: string;
  subtitle?: string;
  run: () => void;
}

interface Props {
  onClose: () => void;
  openTab: OpenTabFn;
  layout: Layout;
  activeLeafId: string | null;
  closeTab: (leafId: string, tabId: string) => void;
  /**
   * The already-filtered nav items (capability gate + custom cell mode) —
   * the palette derives its Navigation commands from these so both surfaces
   * always agree on what's reachable.
   */
  navItems: NavItem[];
}

export function CommandPalette({ onClose, openTab, layout, activeLeafId, closeTab, navItems }: Props) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const items = useMemo<PaletteItem[]>(
    () => [
      ...navItems.map(
        (n): PaletteItem => ({
          id: `go-${n.id}`,
          kind: 'Navigation',
          title: `Go to ${n.label}`,
          subtitle: n.id === 'home' ? 'Home' : 'Navigation',
          run: () => openTab(n.viewId, {}, n.label, {}),
        }),
      ),
      {
        id: 'close-all',
        kind: 'Action',
        title: 'Close all tabs in active pane',
        subtitle: 'Layout action',
        run: () => {
          const leaf = allLeaves(layout).find((l) => l.id === activeLeafId);
          if (leaf) leaf.tabs.filter((t) => !t.pinned).forEach((t) => closeTab(leaf.id, t.id));
        },
      },
    ],
    [navItems, layout, activeLeafId, openTab, closeTab],
  );

  const filtered = useMemo(() => {
    if (!q.trim()) return items.slice(0, 20);
    const ql = q.toLowerCase();
    return items
      .filter(
        (r) =>
          r.title.toLowerCase().includes(ql) ||
          (r.subtitle ?? '').toLowerCase().includes(ql) ||
          r.kind.toLowerCase().includes(ql),
      )
      .slice(0, 40);
  }, [q, items]);

  useEffect(() => setSel(0), [q]);

  const choose = (r: PaletteItem) => {
    r.run();
    onClose();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel((s) => Math.min(s + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel((s) => Math.max(s - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = filtered[sel];
      if (pick) choose(pick);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const kindColor: Record<Kind, string> = {
    Action: 'var(--text-soft)',
    Navigation: 'var(--accent)',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay)',
        display: 'flex',
        justifyContent: 'center',
        paddingTop: '14vh',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 580,
          maxHeight: '60vh',
          background: 'var(--surface-raise)',
          borderRadius: 12,
          boxShadow: 'var(--shadow), 0 0 0 1px var(--border-strong)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 14 }}>🔍</span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search conversations, tasks, credentials, team… or run an action"
            style={{ flex: 1, border: 0, outline: 0, background: 'transparent', fontSize: 14, color: 'var(--text)' }}
          />
          <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, color: 'var(--text-mute)' }}>esc</span>
        </div>
        <div style={{ overflowY: 'auto', padding: '6px 0' }}>
          {filtered.length === 0 && (
            <div style={{ padding: '24px 16px', color: 'var(--text-mute)', fontSize: 13, textAlign: 'center' }}>No results</div>
          )}
          {filtered.map((r, i) => (
            <div
              key={r.id}
              onClick={() => choose(r)}
              onMouseEnter={() => setSel(i)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 16px',
                background: sel === i ? 'var(--accent-soft)' : 'transparent',
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  color: kindColor[r.kind],
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  width: 66,
                }}
              >
                {r.kind}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.title}
                </div>
                {r.subtitle && (
                  <div style={{ fontSize: 11, color: 'var(--text-mute)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.subtitle}
                  </div>
                )}
              </div>
              {sel === i && <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, color: 'var(--text-mute)' }}>↵</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
